import type { TripSidebarController } from "../../hooks/useTripSidebarController";
import type { SortMode } from "../../hooks/usePoiFilters";

export interface ActiveFilter {
  key: string;
  label: string;
}

/**
 * One entry per active filter, in the order the Filters dialog lists them.
 * Search is left out: it has its own field in the toolbar.
 */
export function buildActiveFilters(
  controller: TripSidebarController,
  boundsFilterEnabled: boolean,
): ActiveFilter[] {
  const filters: ActiveFilter[] = [];
  const named = (mode: string, label: string) =>
    mode === "exclude" ? `Not ${label}` : label;

  if (controller.hideScheduled) {
    filters.push({ key: "hide-planned", label: "Hide planned" });
  }
  if (boundsFilterEnabled) {
    filters.push({ key: "bounds", label: "In map area" });
  }
  controller.legFilters.forEach((mode, legId) => {
    const leg = controller.legOptions.find((option) => option.id === legId);
    if (!leg) return;
    filters.push({ key: `leg-${legId}`, label: named(mode, leg.name) });
  });
  controller.tagFilters.forEach((mode, tag) => {
    const label =
      controller.tagFacets.find((facet) => facet.tag === tag)?.label ?? tag;
    filters.push({ key: `tag-${tag}`, label: named(mode, label) });
  });
  controller.collectionFilters.forEach((mode, collectionId) => {
    const collection = controller.collectionOptions.find(
      (option) => option.id === collectionId,
    );
    if (!collection) return;
    filters.push({
      key: `collection-${collectionId}`,
      label: named(mode, collection.name),
    });
  });
  controller.sourceFilters.forEach((mode, source) => {
    filters.push({ key: `source-${source}`, label: named(mode, source) });
  });
  controller.ownerFilters.forEach((mode, ownerId) => {
    const owner = controller.ownerOptions.find(
      (option) => option.id === ownerId,
    );
    if (!owner) return;
    filters.push({ key: `library-${ownerId}`, label: named(mode, owner.name) });
  });
  if (controller.maximumDistanceKm !== null) {
    filters.push({
      key: "maximum-distance",
      label: `Within ${controller.maximumDistanceKm} km`,
    });
  }
  return filters;
}

export const SORT_SUMMARY: Record<SortMode, string> = {
  alpha: "A–Z",
  timeBudget: "by duration",
  recentlyAdded: "by recent",
  distance: "by distance",
};

/** "Unscheduled · Kyoto · by distance": the leading filters plus the sort. */
export function summarizeFilters(
  filters: ActiveFilter[],
  sortMode: SortMode,
): string {
  const labels = filters.map((filter) =>
    filter.key === "hide-planned" ? "Unscheduled" : filter.label,
  );
  const shown = labels.slice(0, 2);
  if (labels.length > shown.length) {
    shown.push(`+${labels.length - shown.length}`);
  }
  return [...shown, SORT_SUMMARY[sortMode]].join(" · ");
}
